import { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import servicesHero from "@/assets/services-hero.jpg";
import { MapPin, Phone, Mail, ChevronDown, ArrowRight, Users, Search, X, Navigation } from "lucide-react";
import { Input } from "@/components/ui/input";
import { attorneys } from "@/data/attorneys";
import OfficeLocationsMap from "@/components/OfficeLocationsMap";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { offices } from "@/data/offices";


const partnerTitles = ["President", "Partner", "Of Counsel"];

function getAttorneysForLocation(locationKey: string) {
  const matched = attorneys.filter(
    (a) => a.location && a.location.split(", ").some((loc) => loc === locationKey)
  );

  // Pin specific attorneys first/last per location
  const pinnedFirstByLocation: Record<string, string[]> = {
    Philadelphia: ["joseph-e-vaughan"],
    Syracuse: ["tracey-a-mclean"],
    Albany: ["zachary-h-pratt"],
    "Palm Beach": ["carlos-j-hernandez", "jeffrey-l-marks"],
  };
  const pinnedLastByLocation: Record<string, string[]> = {
    Marlton: ["adrienne-n-anderson"],
  };

  const pinnedFirstSlugs = pinnedFirstByLocation[locationKey] || [];
  const pinnedLastSlugs = pinnedLastByLocation[locationKey] || [];

  const pinnedFirst = pinnedFirstSlugs
    .map((slug) => matched.find((a) => a.slug === slug))
    .filter(Boolean) as typeof matched;
  const pinnedLast = pinnedLastSlugs
    .map((slug) => matched.find((a) => a.slug === slug))
    .filter(Boolean) as typeof matched;
  const rest = matched.filter((a) => !pinnedFirstSlugs.includes(a.slug) && !pinnedLastSlugs.includes(a.slug));

  const partners = rest
    .filter((a) => a.title && partnerTitles.includes(a.title))
    .sort((a, b) => a.name.localeCompare(b.name));
  const associates = rest
    .filter((a) => !a.title || !partnerTitles.includes(a.title))
    .sort((a, b) => a.name.localeCompare(b.name));

  return [...pinnedFirst, ...partners, ...associates, ...pinnedLast];
}

function LocationAttorneys({ locationKey }: { locationKey: string }) {
  const [open, setOpen] = useState(false);
  const matched = getAttorneysForLocation(locationKey);

  if (matched.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-6">
      <CollapsibleTrigger className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-body text-primary hover:text-primary/80 transition-colors cursor-pointer">
        <Users className="w-3.5 h-3.5" />
        <span>{matched.length} Attorney{matched.length !== 1 ? "s" : ""}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4">
        <div className="grid grid-cols-1 gap-5">
          {matched.map((attorney) => (
            <Link
              key={attorney.slug}
              to={`/team/${attorney.slug}`}
              className="group flex items-center gap-6 p-6 border border-border rounded hover:border-primary hover:bg-card transition-all duration-200"
            >
              {attorney.photo ? (
                <div className="w-24 h-24 rounded-full overflow-hidden shrink-0">
                  <img
                    src={attorney.photo}
                    alt={attorney.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full border border-border flex items-center justify-center shrink-0 group-hover:border-primary transition-colors">
                  <span className="font-heading text-lg text-primary">{attorney.initials}</span>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-lg font-body text-foreground truncate group-hover:text-primary transition-colors">
                  {attorney.name}
                </p>
                {attorney.title && (
                  <p className="text-base font-body text-muted-foreground truncate">{attorney.title}</p>
                )}
              </div>
              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary ml-auto shrink-0 transition-colors" />
            </Link>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

const LocationsPage = () => {
  const [search, setSearch] = useState("");

  const handleMapPinClick = useCallback((locationKey: string) => {
    const locationAliases: Record<string, string> = {
      "Palm Beach Gardens": "Palm Beach",
    };

    const normalizedLocationKey = locationAliases[locationKey] ?? locationKey;
    const office = offices.find((o) => o.locationKey === normalizedLocationKey);
    if (office) {
      const slug = office.locationKey.toLowerCase().replace(/\s+/g, "-");
      setTimeout(() => {
        const el = document.getElementById(`office-${slug}`);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  const filteredOffices = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return offices;
    return offices.filter((office) => {
      const searchable = `${office.city} ${office.address}`.toLowerCase();
      return searchable.includes(q);
    });
  }, [search]);

  return (
    <Layout>
      <SEO
        title="Office Locations | Vaughan McLean"
        description={`Find a Vaughan McLean office near you. With ${offices.length} locations across the United States, our attorneys are ready to serve your legal needs.`}
        jsonLd={offices.map((o) => {
          const lines = o.address.split("\n");
          const last = lines[lines.length - 1] || "";
          const match = last.match(/^(.*),\s*([A-Z]{2})\s+(\d{5})/);
          return {
            "@context": "https://schema.org",
            "@type": "LegalService",
            name: `Vaughan McLean — ${o.city}`,
            telephone: o.phone,
            ...(o as any).fax ? { faxNumber: (o as any).fax } : {},
            address: {
              "@type": "PostalAddress",
              streetAddress: lines.slice(0, -1).join(", "),
              addressLocality: match?.[1] || "",
              addressRegion: match?.[2] || "",
              postalCode: match?.[3] || "",
              addressCountry: "US",
            },
            url: "https://www.vaughanmclean.com/locations",
          };
        })}
      />
      <PageHero backgroundImage={servicesHero} title="Locations" subtitle="Our Offices" />

      <section className="section-dark py-24">
        <div className="container mx-auto px-8 lg:px-16">
          <div className="mb-12 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by city, state, or address..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
            </div>
            {search && (
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.15em] uppercase font-body text-muted-foreground">
                  {filteredOffices.length} location{filteredOffices.length !== 1 ? "s" : ""} found
                </p>
                <button
                  onClick={() => setSearch("")}
                  className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-body text-muted-foreground hover:text-primary transition-colors"
                >
                  <X className="w-3 h-3" /> Clear search
                </button>
              </div>
            )}
          </div>

          <div className="mb-12">
            <OfficeLocationsMap onPinClick={handleMapPinClick} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
            {filteredOffices.map((office, i) => (
              <motion.div
                key={office.city}
                id={`office-${office.locationKey.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-background p-10 scroll-mt-24"
              >
                <h3 className="font-heading text-3xl text-foreground mb-6">{office.city}</h3>

                {/* Google Maps Embed */}
                <div className="w-full aspect-video mb-6 rounded-sm overflow-hidden border border-border">
                  <iframe
                    title={`Map of ${office.city} office`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(office.address.replace(/\n/g, ", "))}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                    allowFullScreen
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex gap-3 items-start">
                    <MapPin className="w-4 h-4 text-primary mt-1 shrink-0" />
                    <p className="text-muted-foreground font-body text-sm whitespace-pre-line">
                      {office.address}
                    </p>
                  </div>
                  <div className="flex gap-3 items-center">
                    <Phone className="w-4 h-4 text-primary shrink-0" />
                    <a href={`tel:${office.phone.replace(/\D/g, '')}`} className="text-muted-foreground font-body text-sm hover:text-primary transition-colors">{office.phone}</a>
                  </div>
                  {office.fax && (
                    <div className="flex gap-3 items-center">
                      <Mail className="w-4 h-4 text-primary shrink-0" />
                      <p className="text-muted-foreground font-body text-sm">Fax: {office.fax}</p>
                    </div>
                  )}
                </div>

                {/* Google Maps link */}
                <div className="flex gap-4 mt-5">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(office.address.replace(/\n/g, ", "))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-body text-primary hover:text-primary/80 transition-colors"
                  >
                    <Navigation className="w-3.5 h-3.5" />
                    Get Directions
                  </a>
                </div>

                <LocationAttorneys locationKey={office.locationKey} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LocationsPage;
